// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Video } from '../types';
import { getGoogle, geocodeAddress } from '../lib/maps/placesClient';
import { useSettings } from './SettingsContext';

type MarkerEntry = {
  id: string;
  position: google.maps.LatLngLiteral;
  title?: string;
};

interface VideoMapProps {
  videos: Video[];
  highlightedVideoId?: string | null;
  onMarkerClick?: (videoId: string) => void;
}

const containerStyle: React.CSSProperties = {
  width: '100%',
  height: '360px',
  borderRadius: '1rem',
  overflow: 'hidden',
};

// Build a geocode query for a video
function videoToQuery(v: Video): string | null {
  const addr = v.restaurant_address && v.restaurant_address !== 'N/A' ? v.restaurant_address : '';
  const name = v.restaurant_name && v.restaurant_name !== 'N/A' ? v.restaurant_name : '';
  const loc = v.restaurant_location && v.restaurant_location !== 'N/A' ? v.restaurant_location : '';
  const q = addr || [name, loc].filter(Boolean).join(' ');
  return q || null;
}

const cache = new Map<string, google.maps.LatLngLiteral>();

const VideoMap: React.FC<VideoMapProps> = ({ videos, highlightedVideoId, onMarkerClick }) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const [ready, setReady] = useState(false);
  const [markers, setMarkers] = useState<MarkerEntry[]>([]);
  const { settings } = useSettings();

  // Load Google Maps API
  useEffect(() => {
    let mounted = true;
    getGoogle()
      .then(() => mounted && setReady(true))
      .catch(() => mounted && setReady(false));
    return () => { mounted = false; };
  }, []);

  // Prepare geocoded markers for current videos
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;

    (async () => {
      const next: MarkerEntry[] = [];
      const slice = videos.slice(0, 50); // cap to avoid heavy geocoding
      for (const v of slice) {
        if (!v.id) continue;
        const q = videoToQuery(v);
        if (!q) continue;
        let pos = cache.get(q);
        if (!pos) {
          try {
            const g = await geocodeAddress(q);
            if (g) {
              pos = g;
              cache.set(q, g);
            }
          } catch {}
        }
        if (pos) next.push({ id: v.id, position: pos, title: v.restaurant_name !== 'N/A' ? v.restaurant_name : undefined });
        if (cancelled) return;
      }
      if (!cancelled) setMarkers(next);
    })();

    return () => { cancelled = true; };
  }, [videos, ready]);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    // Fit to markers initially
    if (markers.length) {
      const bounds = new google.maps.LatLngBounds();
      markers.forEach(m => bounds.extend(m.position));
      map.fitBounds(bounds, 48);
    } else {
      map.setZoom(2);
      map.setCenter({ lat: 0, lng: 0 });
    }
  }, [markers]);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  // When markers change after load, refit
  useEffect(() => {
    if (!mapRef.current || !markers.length) return;
    const bounds = new google.maps.LatLngBounds();
    markers.forEach(m => bounds.extend(m.position));
    mapRef.current.fitBounds(bounds, 48);
  }, [markers]);

  // Pan/zoom to highlighted marker
  useEffect(() => {
    if (!mapRef.current || !highlightedVideoId) return;
    const m = markers.find(mm => mm.id === highlightedVideoId);
    if (m) {
      if (settings.reduce_motion) {
        // Jump directly without animated pan
        mapRef.current.setCenter(m.position);
      } else {
        mapRef.current.panTo(m.position);
      }
      const z = Math.max(12, mapRef.current.getZoom() || 12);
      mapRef.current.setZoom(z);
    }
  }, [highlightedVideoId, markers, settings.reduce_motion]);

  const markerIcon = useMemo(() => {
    const g = (window as any).google as typeof google | undefined;
    const base = g?.maps?.SymbolPath?.CIRCLE;
    return (highlight: boolean) => ({
      path: base ?? 0,
      fillColor: highlight ? '#ef4444' : '#2563eb',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: highlight ? 9 : 6,
    });
  }, [highlightedVideoId]);

  if (!ready) {
    return <div className="text-gray-500 text-center p-4">Cargando mapa…</div>;
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={{ lat: 0, lng: 0 }}
      zoom={2}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{
        disableDefaultUI: true,
        mapTypeId: settings.map_style === 'satellite' ? 'satellite' : 'roadmap',
      }}
    >
      {markers.map(m => (
        <Marker
          key={m.id}
          position={m.position}
          title={m.title}
          onClick={() => onMarkerClick?.(m.id)}
          icon={markerIcon(m.id === highlightedVideoId)}
        />
      ))}
    </GoogleMap>
  );
};

export default VideoMap;
